import {
  CloseButton,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useLimitRequests } from "../../../api/hooks/limitRequest/useLimitRequests.ts";
import { useRRAssignees } from "../../../api/hooks/user/useRRAssignees.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  LimitRequestStatusFilterOptions,
  OrderBy,
  StringFilterMode,
} from "../../../graphql/generated.ts";
import { CustomerOption } from "../../../types/customer.ts";
import {
  LimitRequest,
  limitRequestTypeOptions,
} from "../../../types/limitRequest/limitRequest.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../../utils/filter.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { getUserOptions } from "../../../utils/user.ts";
import { getCURecommendationApiSort } from "../utils.ts";
import { useOngoingRRTableData } from "./hooks/useOngoingRRTableData.tsx";

interface Props {
  companies: CustomerOption[] | undefined;
}

const OngoingRRTable = ({ companies }: Props) => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [{ data, fetching, error }] = useLimitRequests({
    filter: {
      ...filter,
      requestStatus: {
        equals: LimitRequestStatusFilterOptions.OngoingRr,
      },
      limitName: debouncedSearch
        ? {
            contains: debouncedSearch,
            mode: StringFilterMode.Insensitive,
          }
        : undefined,
    },
    orderBy,
    page,
  });

  const [{ data: assigneesData }] = useRRAssignees();

  const assignees = assigneesData?.users?.data;

  const ongoingRRData: LimitRequest[] | undefined | null =
    data?.limitRequests?.data;

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getCURecommendationApiSort(sort));
    },
    [setOrderBy]
  );

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const pageCount = getPagesCount(data?.limitRequests?.total);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter(getLimitRequestApiFilter({ [key]: value }));
  }, []);

  const [columns, rows] = useOngoingRRTableData({
    data: ongoingRRData,
  });

  if (error) {
    notifications.clean();
    notifications.show({
      message: "Something went wrong while trying to fetch Ongoing RR data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={12}>
      <SectionTitle>Ongoing RR</SectionTitle>
      <SimpleGrid
        cols={4}
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        spacing={20}
        verticalSpacing={12}
        pb={8}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Company name"
          placeholder="Select Company name"
          data={getCompanyOptions(companies)}
          onChange={(value) => handleFilterChange("companyName", value)}
        />
        <Select
          size="m"
          clearable
          searchable
          label="RR Assigned"
          placeholder="Select RR Assigned"
          data={getUserOptions(assignees)}
          onChange={(value) =>
            handleFilterChange("rrAssignedId", Number(value))
          }
        />
        <Select
          size="m"
          clearable
          label="Limit Type"
          placeholder="Select Limit Type"
          data={limitRequestTypeOptions}
          onChange={(value) => handleFilterChange("limitRequestType", value)}
        />
        <TextInput
          size="m"
          label="Limit Name"
          placeholder="Enter Limit Name"
          rightSection={
            <CloseButton
              aria-label="Clear input"
              variant="transparent"
              size="md"
              mr="5px"
              style={{
                display: search ? undefined : "none",
              }}
              onClick={() => setSearch("")}
            />
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "createdAt",
          direction: SortDirection.asc,
        }}
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onRowClick={handleRowClik}
        onSortChange={handleSortChange}
      />
    </Flex>
  );
};

export default OngoingRRTable;
